import { useEffect, useMemo, useState } from 'react';
import { get } from '../../../helpers/api';
import { Accordion, AccordionDetails, AccordionSummary, Chip, Stack, Typography } from '../../../ui/core';
import { ExpandMoreIcon } from '../../../ui/icons';
import EventLogTimeline from '../EventLog/EventLogTimeline';
import { useEventLogFilters } from '../EventLog/eventLogFilters';

const ENTITY_ALL = 'all';
const ENTITY_TRIP = 'trip';

const riderNameForRide = (ride) => {
  if (!ride.rider) return null;
  return `${ride.rider.firstName || ''} ${ride.rider.lastName || ''}`.trim() || null;
};

const TripEventLog = ({ trip }) => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [entityFilter, setEntityFilter] = useState(ENTITY_ALL);
  const filters = useEventLogFilters();
  const { queryString } = filters;

  const allRides = useMemo(
    () => [...(trip.rides || []), ...(trip.cancelledRides || [])],
    [trip.rides, trip.cancelledRides],
  );

  const ridersByRideId = useMemo(
    () =>
      Object.fromEntries(
        allRides.map((ride) => [ride.id || ride.rideId, riderNameForRide(ride)]).filter(([rideId]) => rideId),
      ),
    [allRides],
  );

  // Reset to the loading (skeleton) state when the trip changes — but not when
  // only the filter selection changes, so a filter refetch keeps the prior rows
  // visible (under a progress indicator) instead of flashing to the skeleton.
  useEffect(() => {
    setEvents(null);
  }, [trip.id]);

  // Drop the client-side entity selection whenever the trip or the server-side
  // filter selection changes, so a stale ride selection can't leave the timeline
  // showing a confusing empty slice after the returned event set narrows.
  useEffect(() => {
    setEntityFilter(ENTITY_ALL);
  }, [trip.id, queryString]);

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      setRefetching(true);
      setError(false);
      try {
        const response = await get(`/admin/trip/${trip.id}/events${queryString}`);
        if (!cancelled) setEvents(response.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching trip events:', err);
        setError(true);
        setEvents([]);
      } finally {
        if (!cancelled) setRefetching(false);
      }
    };

    fetchEvents();

    return () => {
      cancelled = true;
    };
  }, [trip.id, queryString]);

  const filtered =
    events === null
      ? null
      : events.filter((event) => {
          if (entityFilter === ENTITY_ALL) return true;
          if (entityFilter === ENTITY_TRIP) return event.entityType === 'trip';
          return event.rideId === entityFilter;
        });

  const showFilters = events !== null && events.length > 0;

  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      elevation={0}
      square
      sx={{
        background: 'transparent',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: 0, minHeight: 'auto' }}>
        <Typography sx={{ fontSize: '20px' }}>Event Log</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '12px 0 0 0' }}>
        {showFilters && (
          <Stack direction="row" spacing="8px" flexWrap="wrap" marginBottom="4px">
            <Chip
              label="All"
              size="small"
              variant={entityFilter === ENTITY_ALL ? 'filled' : 'outlined'}
              onClick={() => setEntityFilter(ENTITY_ALL)}
            />
            <Chip
              label="Trip only"
              size="small"
              variant={entityFilter === ENTITY_TRIP ? 'filled' : 'outlined'}
              onClick={() => setEntityFilter(ENTITY_TRIP)}
            />
            {allRides.map((ride) => {
              const rideId = ride.id || ride.rideId;
              if (!rideId) return null;
              return (
                <Chip
                  key={rideId}
                  label={ridersByRideId[rideId] || rideId}
                  size="small"
                  variant={entityFilter === rideId ? 'filled' : 'outlined'}
                  onClick={() => setEntityFilter(rideId)}
                />
              );
            })}
          </Stack>
        )}
        {error ? (
          <Typography variant="body2" color="error.main" sx={{ padding: '8px 0' }}>
            There was an error loading the event log.
          </Typography>
        ) : (
          <EventLogTimeline
            events={filtered}
            loading={events === null}
            refetching={refetching}
            timezone={trip.originCity?.timeZone}
            showEntityColumn
            ridersByRideId={ridersByRideId}
            filters={filters}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TripEventLog;
